import { SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { TextFieldProps, fieldToTextField } from 'formik-mui';


type CollectionItem<IdType extends number | string> = {
  id: IdType;
  label: string
}
type CollectionAutocompleteProps<ItemIdType extends number | string> = {
  items: CollectionItem<ItemIdType>[];
  isLoading: boolean;
  clearable?: boolean
  size?: 'small' | 'medium' | undefined;
  onItemChange?: (item: CollectionItem<ItemIdType> | null) => void;
} & TextFieldProps

export const CollectionAutocomplete = <ItemIdType extends number | string = number | string>(props: CollectionAutocompleteProps<ItemIdType>) => {
  const {
    form: { setFieldValue, initialValues },
    field: { name },
    size = undefined
  } = props;

  const { items, isLoading, clearable = false, onItemChange, ...textFieldProps } = props;

  const initialId: ItemIdType | null = initialValues[name] ?? null;

  const [selectedId, setSelectedId] = useState<ItemIdType | null>(initialId);

  const onChange =
    (_event: SyntheticEvent<Element, Event>, newValue: {id: ItemIdType } | null) => {
      if (!newValue && !clearable) return;
      setSelectedId(newValue ? newValue.id : null);
      onItemChange?.(newValue ? items.find((item) => item.id === newValue.id) ?? null : null);
    };

  const [ open, setOpen ] = useState(false);


  const loading = open && items.length === 0;

  useEffect(() => {
    setFieldValue(name, selectedId ? selectedId : null);
  }, [selectedId, setFieldValue, name]);

  const selectedValue: CollectionItem<ItemIdType> | null = items.find((x:CollectionItem<ItemIdType>) => x.id === selectedId) ?? null;

  return (
    <Autocomplete
      fullWidth
      value={selectedValue}
      options={items}
      disabled={props.disabled}
      disableClearable={!clearable}
      open={open}
      size={size}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => `${option.label}`}
      loading={loading}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      renderInput={(params) => (
        <TextField
          {...fieldToTextField(textFieldProps)}
          name={name}
          {...params}
          disabled={props.disabled}
          label={textFieldProps.label}
          placeholder="Выбрать"
        />
      )}
    />
  );
};
